import { motion } from 'framer-motion';
import { ArrowUpRight } from 'lucide-react';

const studies = [
  {
    industry: 'DTC Apparel',
    brand: 'Northwind Co.',
    metric: '+247%',
    metricLabel: 'engagement in 90 days',
    blurb:
      'Replatformed organic + paid social on SprynAI. AI creative variants tripled CTR while halving production time.',
    tags: ['Instagram', 'TikTok', 'Meta Ads'],
  },
  {
    industry: 'SaaS',
    brand: 'Lumen Analytics',
    metric: '4.6x',
    metricLabel: 'ROAS on LinkedIn',
    blurb:
      'Audience intelligence and account-based retargeting turned LinkedIn from a brand channel into the #1 pipeline source.',
    tags: ['LinkedIn', 'Email', 'ABM'],
  },
  {
    industry: 'Hospitality',
    brand: 'Atlas Resorts',
    metric: '−38%',
    metricLabel: 'cost per booking',
    blurb:
      'AI-tuned Google + Meta campaigns plus weekly auto-reports cut acquisition cost while booking volume grew 2.2×.',
    tags: ['Google Ads', 'Meta Ads', 'Reporting'],
  },
];

export default function CaseStudies() {
  return (
    <section
      id="work"
      className="relative bg-background text-foreground py-24 md:py-32 px-8 md:px-28"
    >
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14"
        >
          <div className="max-w-2xl">
            <div className="liquid-glass inline-flex items-center gap-2 px-3 py-1.5 rounded-lg mb-6">
              <span className="text-xs font-medium text-muted-foreground tracking-wide uppercase">
                Client results
              </span>
            </div>
            <h2 className="text-4xl md:text-6xl font-medium tracking-[-1.5px] leading-[1.05]">
              Real numbers from{' '}
              <span className="font-serif italic font-normal">
                real campaigns.
              </span>
            </h2>
          </div>
          <a
            href="#contact"
            className="inline-flex items-center gap-2 text-sm font-medium text-foreground/90 hover:text-foreground transition-colors"
          >
            See all case studies
            <ArrowUpRight className="h-4 w-4" />
          </a>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {studies.map((s, i) => (
            <motion.article
              key={s.brand}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '-60px' }}
              transition={{ duration: 0.55, delay: i * 0.08 }}
              whileHover={{ y: -5 }}
              className="group relative rounded-2xl border border-border bg-card p-7 md:p-8 overflow-hidden hover:border-white/15 transition-colors transform-gpu"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity"
                style={{
                  background:
                    'radial-gradient(60% 60% at 50% 0%, rgba(255,255,255,0.05) 0%, rgba(255,255,255,0) 70%)',
                }}
              />
              <div className="relative">
                <div className="text-xs uppercase tracking-wider text-muted-foreground mb-6">
                  {s.industry}
                </div>
                <div className="text-5xl md:text-6xl font-medium tracking-[-1.5px] mb-1">
                  {s.metric}
                </div>
                <div className="text-sm text-muted-foreground mb-6">
                  {s.metricLabel}
                </div>
                <p className="text-sm leading-6 text-foreground/85 mb-6">
                  {s.blurb}
                </p>
                <div className="flex flex-wrap gap-2 mb-6">
                  {s.tags.map((t) => (
                    <span
                      key={t}
                      className="text-[11px] tracking-wide px-2 py-0.5 rounded-md border border-border text-muted-foreground"
                    >
                      {t}
                    </span>
                  ))}
                </div>
                <div className="flex items-center justify-between pt-5 border-t border-border">
                  <span className="text-sm font-semibold">{s.brand}</span>
                  <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-foreground transition-colors" />
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
